import { useState, type FormEvent } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

/* ─── design tokens (dark-on-photo) ─── */
const card: React.CSSProperties = {
  background: 'rgba(10, 8, 6, 0.52)',
  border: '1px solid rgba(184, 113, 74, 0.22)',
  borderTop: '1px solid rgba(184, 113, 74, 0.5)',
  backdropFilter: 'blur(22px)',
  WebkitBackdropFilter: 'blur(22px)',
  borderRadius: '4px',
  boxShadow: '0 32px 80px rgba(0,0,0,0.55), 0 4px 16px rgba(0,0,0,0.3)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: '2px',
  padding: '9px 12px',
  fontSize: '14px',
  color: '#f5f1eb',
  outline: 'none',
  transition: 'border-color 0.15s',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '10px',
  fontWeight: 500,
  color: 'rgba(255,255,255,0.5)',
  marginBottom: '6px',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};

export function LoginPage() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      navigate('/');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Falha ao entrar';
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="relative flex min-h-screen items-center justify-center overflow-hidden"
      style={{ fontFamily: '"Instrument Sans", system-ui, sans-serif' }}
    >
      {/* ── Full-page background ── */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: 'url(/sidebar-bg.jpeg)' }}
      />

      {/* ── Gradient overlay: dark vignette ── */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 60% 50%, rgba(8,6,4,0.45) 0%, rgba(8,6,4,0.72) 100%)',
        }}
      />

      {/* ── "RECRUTAMENTO & EXPANSÃO" bottom label ── */}
      <p
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        style={{
          fontSize: '13px',
          color: 'rgba(255,255,255,0.6)',
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        Recrutamento &amp; Expansão
      </p>

      {/* ── Floating card ── */}
      <div
        className="relative z-10 w-full animate-slide-up"
        style={{ ...card, maxWidth: '400px', margin: '0 16px', padding: '44px 40px 40px' }}
      >

        {/* Logo */}
        <div className="flex justify-center mb-10">
          <img
            src="/logo-nobel.png"
            alt="Nobel Capital"
            style={{
              width: '180px',
              filter: 'brightness(0) invert(1) drop-shadow(0 1px 8px rgba(0,0,0,0.4))',
            }}
          />
        </div>

        {/* Heading */}
        <div className="mb-7">
          <h1
            style={{
              fontFamily: '"DM Serif Display", Georgia, serif',
              fontSize: '24px',
              color: '#f5f1eb',
              fontWeight: 400,
              marginBottom: '4px',
            }}
          >
            Bem-vindo
          </h1>
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)' }}>
            Acesse sua conta para continuar.
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label htmlFor="email" style={labelStyle}>Email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
              placeholder="seu@nobelcapital.com.br"
              onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(184,113,74,0.7)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.14)')}
            />
          </div>

          <div>
            <label htmlFor="password" style={labelStyle}>Senha</label>
            <div style={{ position: 'relative' }}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ ...inputStyle, paddingRight: '40px' }}
                placeholder="••••••••"
                onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(184,113,74,0.7)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.14)')}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                style={{
                  position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                  color: 'rgba(255,255,255,0.35)', background: 'none', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center',
                }}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          {error && (
            <p style={{
              padding: '8px 12px', fontSize: '12px',
              background: 'rgba(168,56,48,0.15)', border: '1px solid rgba(168,56,48,0.35)',
              color: '#e8998c', borderRadius: '2px',
            }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              marginTop: '4px', height: '40px', width: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              background: loading ? 'rgba(184,113,74,0.5)' : '#B8714A',
              border: 'none', borderRadius: '2px',
              color: '#fff', fontSize: '13px', fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              letterSpacing: '0.04em',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#9D5C38'; }}
            onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = '#B8714A'; }}
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            {loading ? 'Entrando…' : 'Entrar'}
          </button>
        </form>
      </div>
    </div>
  );
}
